import type { ValidationResult } from "@/services/QuizValidationService";
import {
  InteractiveElementStatusKind,
  type InteractiveElementStatus,
} from "@/lib/interactiveElementState";

/** A learner's saved validated submission for one quiz entry (input + last verdict). */
export interface ValidationSubmission {
  userInput: string;
  correct: boolean | null;
  grade?: string;
  feedback: string;
  attemptCount?: number;
  maxAttempts?: number | null;
}

/** Feedback shown once the server refuses a submission for lack of attempts. */
export const NO_ATTEMPTS_LEFT_FEEDBACK =
  "Für diese Aufgabe sind keine Versuche mehr übrig.";

/** The attempt budget of a quiz slot, as reported by the server. */
export interface AttemptBudget {
  attemptCount?: number;
  maxAttempts?: number | null;
}

/**
 * Thrown when the server refuses a submission because the slot's attempts are
 * used up. Reachable even when the UI believes attempts are left — a second tab
 * or cleared local state can put the two out of step.
 *
 * Lives here rather than in QuizValidationService so quiz components can catch it
 * without pulling the API module (and its auth setup) into their tests.
 */
export class ValidationAttemptsExceededError extends Error {
  constructor(
    readonly attemptCount: number,
    readonly maxAttempts: number | null
  ) {
    super("attempts_exceeded");
    this.name = "ValidationAttemptsExceededError";
  }
}

/**
 * Submissions the learner has left for a slot, or null when the type is
 * uncapped — which is also what a response predating the attempt cap looks
 * like, so callers treat null as "no retry gating".
 */
export const attemptsLeft = (budget: AttemptBudget): number | null => {
  if (budget.maxAttempts == null) return null;
  return Math.max(budget.maxAttempts - (budget.attemptCount ?? 0), 0);
};

/**
 * Whether a graded answer may be submitted again: only a wrong one, and only
 * while the server still grants attempts.
 */
export const canRetry = (
  budget: AttemptBudget & { correct: boolean | null }
): boolean => {
  if (budget.correct === true) return false;
  const left = attemptsLeft(budget);
  return left !== null && left > 0;
};

/**
 * Whether a graded slot has come to rest: either the learner got it right or
 * the server granted the last attempt. Uncapped types never settle on their
 * own — only a correct answer ends them.
 */
export const isResolved = (
  budget: AttemptBudget & { correct: boolean | null }
): boolean => budget.correct === true || attemptsLeft(budget) === 0;

/**
 * Label for the retry CTA. Passing the budget names the attempt the click is
 * about to spend, for quizzes that carry the count on the button rather than
 * in a banner above it; without one it stays the bare action label.
 */
export const retryLabel = (budget?: AttemptBudget): string => {
  if (budget?.maxAttempts == null || budget.attemptCount == null) {
    return "Erneut versuchen";
  }
  return `Erneut versuchen · Versuch ${budget.attemptCount + 1} von ${budget.maxAttempts}`;
};

/** Re-seeds an editor/textarea state map (per entry index) from saved submissions. */
export const seedInputs = (
  submissions: Record<number, ValidationSubmission> | undefined
): Record<number, string> => {
  const seed: Record<number, string> = {};
  for (const [index, submission] of Object.entries(submissions ?? {})) {
    seed[Number(index)] = submission.userInput;
  }
  return seed;
};

/** Returns an InProgress status override when the learner has saved submissions, undefined otherwise. */
export const inProgressFromSubmissions = (
  submissions: Record<number, ValidationSubmission>
): InteractiveElementStatus | undefined =>
  Object.keys(submissions).length > 0
    ? { kind: InteractiveElementStatusKind.InProgress, partial: null }
    : undefined;

/** Re-seeds the verdict map (per entry index) from saved submissions. */
export const seedResults = (
  submissions: Record<number, ValidationSubmission> | undefined
): Record<number, ValidationResult> => {
  const seed: Record<number, ValidationResult> = {};
  for (const [index, submission] of Object.entries(submissions ?? {})) {
    seed[Number(index)] = {
      correct: submission.correct,
      grade: submission.grade,
      feedback: submission.feedback,
      // Carried along so a reopened editor knows how many tries are left
      // before the learner submits again.
      attemptCount: submission.attemptCount,
      maxAttempts: submission.maxAttempts,
    };
  }
  return seed;
};

type FeedbackDisplay = { label: string; className: string };

// The backend grades LLM-validated code quizzes on a four-level scale and
// sends the normalized grade key alongside correct/feedback.
const GRADE_DISPLAY: Record<string, FeedbackDisplay> = {
  richtig: {
    label: "Richtig",
    className: "border-success-border bg-success-light text-success-foreground",
  },
  gerade_noch_richtig: {
    label: "Überwiegend richtig",
    className: "border-green-400 bg-green-50 text-green-800",
  },
  gerade_noch_falsch: {
    label: "Überwiegend falsch",
    className: "border-amber-400 bg-amber-50 text-amber-900",
  },
  falsch: {
    label: "Falsch",
    className: "border-error-border bg-error-light text-error-foreground",
  },
};

/** Returns label and Tailwind classes for the feedback box, driven by grade when available. */
export const getFeedbackDisplay = (
  result: ValidationResult
): FeedbackDisplay => {
  if (result.grade && result.grade in GRADE_DISPLAY) {
    return GRADE_DISPLAY[result.grade];
  }
  if (result.correct === true) {
    return GRADE_DISPLAY.richtig;
  }
  if (result.correct === false) {
    return GRADE_DISPLAY.falsch;
  }
  return { label: "Feedback", className: "border-border bg-muted/40" };
};
